import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import LoadingSpinner from '../components/LoadingSpinner';
import { CitationAssessment } from '../components/CitationAssessment';

const card = 'bg-card text-card-foreground border border-border rounded-lg shadow-sm';
const primaryBtn = 'bg-primary text-primary-foreground rounded-lg font-semibold hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed';
const quietBtn = 'border border-border bg-card text-foreground rounded-lg font-semibold hover:bg-accent';

function lockLabel(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime()) || date.getFullYear() < 2000) return null;
  return date.toLocaleDateString();
}

function TrackerHeader({ trackers, selectedId, onSelect, onAdd, children }) {
  const selected = trackers.find((tracker) => tracker.id === selectedId) || trackers[0];

  return (
    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
      <div className="flex-1 min-w-0">
        <h1 className="text-3xl font-bold mb-2">AI Citation Tracker</h1>
        {trackers.length > 1 ? (
          <select
            value={selectedId}
            onChange={(event) => onSelect(event.target.value)}
            className="px-3 py-1.5 border border-input bg-background text-foreground rounded-md text-sm max-w-full"
          >
            {trackers.map((tracker) => (
              <option key={tracker.id} value={tracker.id}>
                {tracker.url}{tracker.status === 'active' ? '' : ' (unpaid)'}
              </option>
            ))}
          </select>
        ) : (
          <p className="text-muted-foreground">Tracking: {selected?.url}</p>
        )}
      </div>
      <div className="flex gap-2 shrink-0">
        <button
          type="button"
          onClick={onAdd}
          className={`${quietBtn} px-4 py-2 text-sm`}
        >
          Add a site
        </button>
        {children}
      </div>
    </div>
  );
}

export default function CitationTrackerDashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [trackers, setTrackers] = useState([]);
  const [selectedTrackerId, setSelectedTrackerId] = useState(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [runningNow, setRunningNow] = useState(false);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    fetchTrackers();
  }, []);

  useEffect(() => {
    if (selectedTrackerId) {
      fetchDashboard(selectedTrackerId);
    }
  }, [selectedTrackerId]);

  useEffect(() => {
    if (!selectedTrackerId || data?.tracker?.status !== 'active' || data?.latestRun) return undefined;
    const timer = setInterval(() => {
      fetchDashboard(selectedTrackerId);
    }, 8000);
    return () => clearInterval(timer);
  }, [selectedTrackerId, data?.tracker?.status, data?.latestRun]);

  const fetchTrackers = async () => {
    try {
      const response = await fetch('/api/v1/citation-tracker/trackers', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
      const result = await response.json();

      if (result.success && result.data.trackers.length > 0) {
        setTrackers(result.data.trackers);
        setSelectedTrackerId(result.data.trackers[0].id);
      } else {
        setLoading(false);
      }
    } catch (err) {
      setError('Failed to load trackers');
      setLoading(false);
    }
  };

  const fetchDashboard = async (trackerId) => {
    try {
      const response = await fetch(`/api/v1/citation-tracker/dashboard?trackerId=${trackerId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
      const result = await response.json();

      if (result.success) {
        setData(result.data);
      } else {
        setError(result.error || 'Failed to load dashboard');
      }
    } catch (err) {
      setError('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const selectTracker = (trackerId) => {
    setData(null);
    setLoading(true);
    setSelectedTrackerId(trackerId);
  };

  const addSite = () => navigate('/citation-tracker/onboard?add=1');

  const handlePay = async () => {
    if (!selectedTrackerId || paying) return;

    setPaying(true);
    setError(null);
    try {
      const response = await fetch('/api/v1/subscription/create-checkout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({
          tier: 'citation_tracker',
          trackerId: selectedTrackerId,
          successUrl: `${window.location.origin}/citation-tracker?setup=complete`,
          cancelUrl: `${window.location.origin}/citation-tracker`
        })
      });
      const result = await response.json();

      if (result.success && result.data?.url) {
        window.location.href = result.data.url;
        return;
      }

      setError(result.error || 'Failed to start checkout');
    } catch (err) {
      setError('Failed to start checkout');
    } finally {
      setPaying(false);
    }
  };

  const handleRunNow = async () => {
    if (!selectedTrackerId || runningNow) return;

    setRunningNow(true);
    try {
      const response = await fetch('/api/v1/citation-tracker/trigger-run', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({ trackerId: selectedTrackerId })
      });
      const result = await response.json();

      if (result.success) {
        alert('Run started! Results will appear in a few minutes.');
        setTimeout(() => fetchDashboard(selectedTrackerId), 5000);
      } else {
        alert(result.error || 'Failed to start run');
      }
    } catch (err) {
      alert('Failed to start run');
    } finally {
      setRunningNow(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded dark:bg-red-500/15 dark:border-red-400/40 dark:text-red-200">
          {error}
        </div>
      </div>
    );
  }

  if (!trackers || trackers.length === 0) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className={`${card} p-8 text-center`}>
          <h2 className="text-2xl font-bold mb-4">No Citation Tracker Found</h2>
          <p className="text-muted-foreground mb-6">Get started by setting up your citation tracker.</p>
          <button
            onClick={() => navigate('/citation-tracker/onboard')}
            className={`${primaryBtn} px-6 py-3`}
          >
            Set Up Tracker
          </button>
        </div>
      </div>
    );
  }

  if (!data || !data.tracker) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  const { tracker, latestRun, previousRun, weekOverWeekChange, runs, latestAnswers, engineMetrics } = data;

  if (!latestRun) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <TrackerHeader
          trackers={trackers}
          selectedId={selectedTrackerId}
          onSelect={selectTracker}
          onAdd={addSite}
        />

        <div className="bg-secondary border border-border text-secondary-foreground px-6 py-8 rounded-lg text-center">
          <div className="text-4xl mb-4">📊</div>
          <h2 className="text-xl font-semibold mb-2">
            {tracker.status === 'active' ? 'First run underway' : 'First run waiting'}
          </h2>
          <p className="mb-4">
            {tracker.status === 'active'
              ? 'Your first citation run is starting now. It usually takes a few minutes.'
              : 'Your first citation run starts as soon as the subscription is active.'}
          </p>
          <p className="text-sm">After this, the tracker runs every Monday at 9am CT. You'll get an email when the results are ready.</p>
          {tracker.status !== 'active' && (
            <button
              type="button"
              onClick={handlePay}
              disabled={paying}
              className={`${primaryBtn} mt-6 px-6 py-3`}
            >
              {paying ? 'Opening checkout...' : 'Continue to payment'}
            </button>
          )}
        </div>

        <div className={`mt-6 ${card} p-6`}>
          <h3 className="font-semibold mb-3">Your Prompts</h3>
          <ul className="space-y-2">
            {tracker.prompts.map((prompt, i) => (
              <li key={i}>{i + 1}. {prompt}</li>
            ))}
          </ul>
          {lockLabel(tracker.prompts_locked_until) && (
            <p className="text-sm text-muted-foreground mt-4">
              Prompts are locked until {lockLabel(tracker.prompts_locked_until)}
            </p>
          )}
        </div>
      </div>
    );
  }

  const citationSharePercent = (latestRun.citation_share * 100).toFixed(1);
  const changePercent = weekOverWeekChange?.citation_share_change 
    ? (weekOverWeekChange.citation_share_change * 100).toFixed(1)
    : null;

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <TrackerHeader
        trackers={trackers}
        selectedId={selectedTrackerId}
        onSelect={selectTracker}
        onAdd={addSite}
      >
        <button
          onClick={handleRunNow}
          disabled={runningNow}
          className={`${primaryBtn} px-4 py-2 text-sm`}
        >
          {runningNow ? 'Running...' : 'Run Now'}
        </button>
      </TrackerHeader>

      <CitationAssessment assessment={data.assessment} />

      <div className="grid md:grid-cols-3 gap-6 mb-6">
        <div className={`${card} p-6`}>
          <h3 className="text-sm text-muted-foreground mb-2">Citation Share</h3>
          <div className="text-4xl font-bold mb-1">{citationSharePercent}%</div>
          {changePercent && (
            <div className={`text-sm font-semibold ${parseFloat(changePercent) >= 0 ? 'text-green-500' : 'text-red-400'}`}>
              {parseFloat(changePercent) > 0 ? '+' : ''}{changePercent}% vs last week
            </div>
          )}
          <div className="text-xs text-muted-foreground mt-2">N = {latestRun.ok_answers}</div>
          {latestRun.ok_answers < 50 && (
            <div className="text-xs text-amber-500 mt-1">⚠️ Small sample size</div>
          )}
          {data.isAdmin && latestRun.total_cost_usd && (
            <div className="text-xs text-muted-foreground mt-2 font-mono">
              COGS: ${Number(latestRun.total_cost_usd).toFixed(2)}
            </div>
          )}
        </div>

        <div className={`${card} p-6`}>
          <h3 className="text-sm text-muted-foreground mb-2">Mention Rate</h3>
          <div className="text-4xl font-bold mb-1">{(latestRun.mention_rate * 100).toFixed(1)}%</div>
          <div className="text-sm text-muted-foreground">{latestRun.mentioned_count} / {latestRun.ok_answers}</div>
        </div>

        <div className={`${card} p-6`}>
          <h3 className="text-sm text-muted-foreground mb-2">URL Citation Rate</h3>
          {latestRun.url_citation_rate !== null ? (
            <>
              <div className="text-4xl font-bold mb-1">{(latestRun.url_citation_rate * 100).toFixed(1)}%</div>
              <div className="text-sm text-muted-foreground">{latestRun.cited_count} / {latestRun.citable_count || latestRun.ok_answers}</div>
            </>
          ) : (
            <div className="text-muted-foreground text-sm">Mention-only engines</div>
          )}
        </div>
      </div>

      {engineMetrics && (
        <div className={`${card} p-6 mb-6`}>
          <h2 className="text-xl font-semibold mb-4">Performance by Engine</h2>
          <div className="grid md:grid-cols-5 gap-4">
            {['openai', 'perplexity', 'gemini', 'dataforseo', 'meta'].map(engine => {
              const metrics = engineMetrics[engine];
              if (!metrics) {
                return (
                  <div key={engine} className="bg-muted rounded-lg p-4">
                    <h3 className="font-semibold mb-2 capitalize">{engine === 'dataforseo' ? 'Google AIO' : engine}</h3>
                    <div className="text-muted-foreground text-sm">Not configured</div>
                  </div>
                );
              }

              if (metrics.ok_count === 0) {
                return (
                  <div key={engine} className="bg-muted rounded-lg p-4">
                    <h3 className="font-semibold mb-2 capitalize">{engine === 'dataforseo' ? 'Google AIO' : engine}</h3>
                    <div className="text-muted-foreground text-sm">No successful queries</div>
                  </div>
                );
              }

              const isMentionOnly = engine === 'meta';
              const engineLabel = engine === 'dataforseo' ? 'Google AIO' : engine === 'meta' ? 'Meta AI' : engine;
              
              return (
                <div key={engine} className="bg-muted rounded-lg p-4">
                  <h3 className="font-semibold mb-2 capitalize">{engineLabel}</h3>
                  {isMentionOnly && <div className="text-xs text-amber-500 mb-1">Mention-only</div>}
                  <div className="text-2xl font-bold">{(metrics.citation_share * 100).toFixed(1)}%</div>
                  <div className="text-xs text-muted-foreground mt-1">N = {metrics.ok_count}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className={`${card} p-6 mb-6`}>
        <h2 className="text-xl font-semibold mb-4">Prompt × Engine Matrix (Latest Week)</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left p-2">Prompt</th>
                <th className="text-center p-2">ChatGPT</th>
                <th className="text-center p-2">Perplexity</th>
                <th className="text-center p-2">Gemini</th>
                <th className="text-center p-2">Google AIO</th>
                <th className="text-center p-2">Meta AI</th>
              </tr>
            </thead>
            <tbody>
              {tracker.prompts.map((prompt, promptIdx) => (
                <tr key={promptIdx} className="border-b border-border hover:bg-muted">
                  <td className="p-2">{prompt.substring(0, 50)}...</td>
                  {['openai', 'perplexity', 'gemini', 'dataforseo', 'meta'].map(engine => {
                    const answers = latestAnswers?.filter(a => a.prompt_index === promptIdx && a.engine === engine) || [];
                    const hit = answers.find(a => (a.mentioned || a.cited) && a.answer_text)
                      || answers.find(a => a.mentioned || a.cited);
                    const readable = answers.find(a => a.status === 'ok' && a.answer_text);
                    const notConfigured = answers.length === 0 || answers.every(a => a.status === 'not_configured');
                    const noOverview = answers.length > 0 && answers.every(a => a.status === 'no_overview');

                    return (
                      <td key={engine} className="p-2 text-center">
                        {notConfigured ? (
                          <span className="text-muted-foreground text-xs">Not configured</span>
                        ) : noOverview ? (
                          <span className="text-muted-foreground">—</span>
                        ) : hit ? (
                          <button
                            onClick={() => setSelectedAnswer(hit)}
                            className="text-green-500 font-bold hover:underline"
                          >
                            ✓
                          </button>
                        ) : readable ? (
                          <button
                            type="button"
                            onClick={() => setSelectedAnswer(readable)}
                            className="text-muted-foreground hover:text-foreground"
                            aria-label="View answer"
                            title="The engine answered. This site was not named or linked."
                          >
                            ○
                          </button>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className={`${card} p-6`}>
        <h2 className="text-xl font-semibold mb-4">Weekly Trend</h2>
        <div className="space-y-2">
          {runs.slice(0, 8).reverse().map((run) => (
            <div key={run.id} className="flex items-center gap-4">
              <div className="w-24 text-sm text-muted-foreground">{new Date(run.week_start).toLocaleDateString()}</div>
              <div className="flex-1 bg-muted rounded-full h-3 overflow-hidden">
                <div
                  className="bg-primary h-full rounded-full transition-all"
                  style={{ width: `${(run.citation_share * 100)}%` }}
                ></div>
              </div>
              <div className="w-16 text-sm font-semibold">{(run.citation_share * 100).toFixed(1)}%</div>
            </div>
          ))}
        </div>
      </div>

      {selectedAnswer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={() => setSelectedAnswer(null)}>
          <div className="modal-content text-card-foreground max-w-2xl w-full overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-xl font-semibold">Answer Details</h3>
              <button onClick={() => setSelectedAnswer(null)} className="text-muted-foreground hover:text-foreground">✕</button>
            </div>

            <div className="mb-4">
              <div className="text-sm text-muted-foreground mb-1">Prompt</div>
              <div className="font-medium">{selectedAnswer.prompt}</div>
            </div>

            <div className="mb-4">
              <div className="text-sm text-muted-foreground mb-1">Engine</div>
              <div className="font-medium capitalize">{selectedAnswer.engine}</div>
            </div>

            {!selectedAnswer.mentioned && !selectedAnswer.cited && (
              <p className="mb-4 text-sm text-muted-foreground">
                {(tracker.brand_aliases && tracker.brand_aliases[0]) || 'This site'} was not named or linked in this answer. The sources below are the ones the engine used.
              </p>
            )}

            <div className="mb-4">
              <div className="text-sm text-muted-foreground mb-1">Answer</div>
              <div className="bg-muted p-4 rounded border border-border whitespace-pre-wrap">
                {selectedAnswer.answer_text || 'No answer text'}
              </div>
            </div>

            {selectedAnswer.source_urls && selectedAnswer.source_urls.length > 0 && (
              <div>
                <div className="text-sm text-muted-foreground mb-1">Source URLs</div>
                <ul className="space-y-1">
                  {selectedAnswer.source_urls.map((url, i) => (
                    <li key={i} className="text-sm">
                      <a href={url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline break-all">
                        {url}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
